import { jobApi } from "@/rest/modules/job-api";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

const jobKeys = {
  all: ['jobs'] as const,
  job: (jobId: string) => [...jobKeys.all, jobId] as const,
};

export const useGetJobStatus = (jobId: string) => {
  return useQuery({
    queryKey: jobKeys.job(jobId),
    queryFn: () => jobApi.getJobStatus(jobId),
    enabled: !!jobId,
  });
};

interface UsePollingJobOptions {
  interval?: number;
}

export const usePollingJob = (jobId: string, options: UsePollingJobOptions = {}) => {
  const { interval = 1000 } = options;
  const [isPolling, setIsPolling] = useState(true);

  const { data: jobStatus, error, refetch } = useGetJobStatus(jobId);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const jobStatusRef = useRef(jobStatus);
  jobStatusRef.current = jobStatus;

  useEffect(() => {
    if (!jobId) return;

    const intervalId = setInterval(() => {
      const current = jobStatusRef.current;
      if (current?.status === 'completed' || current?.status === 'failed') {
        clearInterval(intervalId);
        setIsPolling(false);
        return;
      }
      refetchRef.current();
    }, interval);

    return () => clearInterval(intervalId);
  }, [jobId, interval]);

  return { data: jobStatus, isLoading: isPolling, error, refetch };
};